import { useCallback, useEffect, useRef } from 'react';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';
import { useAnytimeTicketStore } from '../State/AnytimeTicketContext';

interface Props {
	isOpen: boolean;
	onActualClose: () => void;
}

export default function useAnytimeTicketNavigationGuard({ isOpen, onActualClose }: Props) {
	const isNavBlocked = useAnytimeTicketStore(s => s.isNavBlocked);
	const abortPurchase = useAnytimeTicketStore(s => s.abortPurchase);
	const isShowingConfirmRef = useRef(false);
	const shouldBlockRef = useRef(isNavBlocked);

	useEffect(() => {
		shouldBlockRef.current = isNavBlocked;
	}, [isNavBlocked]);

	const handleConfirmLeave = useCallback(async () => {
		if (isShowingConfirmRef.current) return false;

		isShowingConfirmRef.current = true;
		try {
			await newConfirmModal('Are you sure?', 'You will lose your progress on this purchase.', {
				confirmText: 'Leave',
				cancelText: 'Stay',
			});
			abortPurchase();
			onActualClose();
			return true;
		} catch {
			return false;
		} finally {
			isShowingConfirmRef.current = false;
		}
	}, [abortPurchase, onActualClose]);

	const handleClose = useCallback(() => {
		if (isNavBlocked) {
			handleConfirmLeave();
		} else {
			onActualClose();
		}
	}, [isNavBlocked, handleConfirmLeave, onActualClose]);

	// Browser history pushState to intercept back button
	useEffect(() => {
		if (isOpen && isNavBlocked) {
			window.history.pushState({ anytimeTicketModal: true }, '');
			return () => {
				if (window.history.state?.anytimeTicketModal) {
					window.history.back();
				}
			};
		}
		return undefined;
	}, [isOpen, isNavBlocked]);

	// Popstate (Back button) handling
	useEffect(() => {
		if (!isOpen || !isNavBlocked) return undefined;

		const handlePopState = async () => {
			if (!shouldBlockRef.current || isShowingConfirmRef.current) return;

			// Put the state back immediately to prevent URL change
			window.history.pushState({ anytimeTicketModal: true }, '');

			const confirmed = await handleConfirmLeave();
			if (confirmed) {
				window.history.back(); // Remove our pushed state if they actually left
			}
		};

		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			e.preventDefault();
			e.returnValue = '';
		};

		window.addEventListener('popstate', handlePopState);
		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			window.removeEventListener('popstate', handlePopState);
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [isOpen, isNavBlocked, handleConfirmLeave]);

	return { handleClose };
}
